// @flow
import React from 'react';

import { FormattedMessage } from 'react-intl';
import {
  postPaymentExecute,
  postPaymentExecuteErrorFn,
  postPaymentExecuteUIErrorFn,
  notFoundLibraryError,
} from '../../duck';
import analytics from '../../../../../analytics';

import Visa from '../../../../../assets/img/payment-methods/visa.png';
import Discover from '../../../../../assets/img/payment-methods/discover.png';
import JCB from '../../../../../assets/img/payment-methods/jcb.png';
import MasterCard from '../../../../../assets/img/payment-methods/mastercard.png';
import AmericanExpress from '../../../../../assets/img/payment-methods/american.png';
import DinersClub from '../../../../../assets/img/payment-methods/diners.png';
import logosPayments from '../../../../../assets/img/payment-methods/logos_payments.png';

import type { ImplementationType, PaymentImpl } from '../index';
import type { Order } from '../../../../../entities';
import type { RootState } from '../../../../index';
import type { BillingAddressReq } from '../../api';
import { Default, Mobile } from '../../../../../utils/responsive';


type BSError = {
  errorCode: string;
  errorDescription: string;
  eventType: string;
  tagId?: string;
};

const style = {
  label: {
    fontSize: '12px',
    lineHeight: '16px',
    color: '#656565'
  },
  inputBox: {
    height: '40px',
    border: '1px solid #ababab',
    borderRadius: '5px',
    paddingLeft: '15px',
    paddingRight: '80px',
    marginTop: '8px',
  },

  inputError: {
    borderColor: '#ff7061',
  },

  errorLabel: {
    color: '#ff7061',
    marginTop: '8px',
    display: 'none',
    fontSize: '12px',
    lineHeight: '16px',
    fontWeight: '400',
  }
};

const cardUrl = {
  Visa,
  Discover,
  JCB,
  MasterCard,
  AmericanExpress,
  DinersClub,
};

export default class BlueSnapPayment implements PaymentImpl {
  type: ImplementationType = 'bluesnap';
  uiId: string = 'bluesnapUI';
  order: Order;
  dispatch: Function;
  matchMedia: any;
  // Custom properties
  domainCountry: string;

  handleFocus = (tagId: string) => {
    const inputBox = document.getElementById(tagId);
    const errorLabel = document.getElementById(`${tagId}Error`);
    if (inputBox && inputBox.style.borderColor) {
      inputBox.style.borderColor = '#ababab';
    }
    if (errorLabel && errorLabel.style.display === 'block') {
      errorLabel.style.display = 'none';
    }
  };

  handleBlur = (tagId: string) => {

  };

  handleError = (tagId: string) => {
    //console.log("error", tagId);
  };

  handleType = (tagId: string, cardType: string) => {
    const $cardLogo = document.querySelector('#card-logo');
    if ($cardLogo) {
      if (cardUrl[cardType]) {
        $cardLogo.setAttribute('src', cardUrl[cardType]);
        $cardLogo.style.display = 'inline-block';
      } else {
        $cardLogo.style.display = 'none';
      }
    }
  };

  handleValid = () => {

  };

  setUpPayment(order: Order, dispatch: Function, rootState: RootState) {
    this.order = order;
    this.dispatch = dispatch;
    this.domainCountry = rootState.locale.domainCountry || '';

    if (!window.bluesnap) {
      dispatch(notFoundLibraryError({
        code: 404,
        name: 'notFoundLibraryError',
        url: '',
      }));
      return;
    }

    const bsObj = {
      onFieldEventHandler: {
        onFocus: this.handleFocus,
        onBlur: this.handleBlur,
        onError: this.handleError,
        onType: this.handleType,
        onValid: this.handleValid,
      },
      style: {
        'input': {
          'height': '40px',
          'color': '#121212',
          'font-weight': '300',
          'font-size': '16px'
        },
        '#year': {
          //"width": "30px",
          'left': '41px!important',
        },
        'span': {
          'left': '31px',
          'line-height': '34px',
        },
      },
      ccnPlaceHolder: '1234 5678 9012 3456',
      cvvPlaceHolder: '123',
      expPlaceHolder: 'MM / YY',
      expDropDownSelector: false
    };

    (window.bluesnap: any).hostedPaymentFieldsCreation(order.invoice.payment.paymentData.token, bsObj);
  }

  executeUIAction = (billingAddress: BillingAddressReq) => {
    const order = this.order;
    analytics.fireCustomEvent('Checkout Step Completed', {
      category: 'Checkout Step',
      action: 'Completed',
      label: order.id,
      user_type: 'purchaser',
      user_referrer: window.localStorage.getItem('userReferrer') || '',
      landing_url: window.localStorage.getItem('landingUrl') || '',
      order_id: order.id,
      order_formatted_id: order.id,
      payment_method: order.invoice && order.invoice.payment ? order.invoice.payment.method : '',
      payment_sub_method: order.invoice && order.invoice.payment ? order.invoice.payment.subtype : '',
      domain_country: this.domainCountry,
      type: 'sync',
      step: 1,
      shipping_method: order.serviceTier,
    });
    (window.bluesnap: any).submitCredentials((callback) => {
      if (null !== callback.cardData) {
        analytics.fireCustomEvent('Payment Info Entered', {
          category: 'Payment Info',
          action: 'Entered',
          label: order.id,
          user_type: 'purchaser',
          user_referrer: window.localStorage.getItem('userReferrer') || '',
          landing_url: window.localStorage.getItem('landingUrl') || '',
          order_id: order.id,
          order_formatted_id: order.id,
          payment_method: order.invoice && order.invoice.payment ? order.invoice.payment.method : '',
          payment_sub_method: order.invoice && order.invoice.payment ? order.invoice.payment.subtype : '',
          domain_country: this.domainCountry,
          type: 'sync',
          step: 1,
          shipping_method: order.serviceTier,
        });
        this.dispatch(postPaymentExecute({
          method: order.invoice.payment.method,
          type: order.invoice.payment.subtype,
          billingAddress: billingAddress,
        }, order));
      } else {
        /*  errorCode returns:
            "001" --> "Please enter a valid credit card number";
            "002" --> "Please enter the CVV/CVC of your card";
            "003" --> "Please enter your credit card's expiration date";
            "22013" --> "CC type is not supported by the merchant";
            "14040" --> " Token is expired";
            "14041" --> " Could not find token";
            "14042" --> " Token is not associated with a payment method, please verify your client integration or contact BlueSnap support";
            "400" --> "Session expired please refresh page to continue";
            "403", "404", "500" --> "Internal server error please try again later";
        */
        const errors: BSError[] = callback.error;
        /*const errors: BSError[] = [
          {
            errorCode: '400',
            errorDescription: 'aasdasasd',
            eventType: 'error',
          },
        ];*/
        if (errors) {
          const errorField: ?BSError = errors.find((er: BSError) =>
            er.errorCode === '001' || er.errorCode === '002' || er.errorCode === '003');
          const baseError: { [key: string]: string } = {
            name: 'BLUESNAP_TRANSACTION_ERROR',
            url: 'https://developers.bluesnap.com/docs/build-a-form',
          };
          const error500 = errors.find((e: BSError) => parseInt(e.errorCode) / 100 === 5);
          const familyError400 = errors.find((e: BSError) => parseInt(e.errorCode) / 100 === 4);
          if (errorField) {
            this.dispatch(postPaymentExecuteUIErrorFn({
              code: errorField.errorCode,
              name: errorField.tagId || '',
            }));
          } else if (error500) {
            this.dispatch(postPaymentExecuteErrorFn({ ...baseError, code: error500.errorCode }));
          } else if (familyError400) {
            this.dispatch(postPaymentExecuteErrorFn({ ...baseError, code: familyError400.errorCode }));
          } else {
            this.dispatch(postPaymentExecuteErrorFn({ ...baseError, code: errors[0].errorCode }));
          }

          errors.forEach((e: BSError) => {
            const inputBox = e.tagId && document.getElementById(e.tagId);
            const errorLabel = e.tagId && document.getElementById(`${e.tagId}Error`);
            if (inputBox && errorLabel) {
              inputBox.style.borderColor = '#ff7061';
              errorLabel.style.display = 'block';
            }
          });
        }
      }
    });
  };

  setStyleFromMatchMedia(matchMedia: any) {
    const wrapGroups = document.getElementById('wrapGroups');
    const expGroup = document.getElementById('expGroup');
    const cvvGroup = document.getElementById('cvvGroup');
    if (wrapGroups && expGroup && cvvGroup) {
      if (matchMedia.matches) {
        expGroup.style.width = '100%';
        expGroup.style.marginTop = '16px';
        cvvGroup.style.width = '100%';
        cvvGroup.style.marginTop = '16px';
      } else {
        expGroup.style.marginTop = '16px';
        expGroup.style.width = '238px';
        expGroup.style.display = 'inline-block';
        expGroup.style.verticalAlign = 'top';
        expGroup.style.marginRight = '16px';
        cvvGroup.style.marginTop = '16px';
        cvvGroup.style.width = '238px';
        cvvGroup.style.display = 'inline-block';
        cvvGroup.style.verticalAlign = 'top';
      }
    }
  }

  generateCustomUI = () => {
    this.matchMedia = window.matchMedia('(max-width: 992px)');
    this.setStyleFromMatchMedia(matchMedia);
    this.matchMedia.addListener(this.setStyleFromMatchMedia);

    const imgStyle = { height: '32px', position: 'absolute', right: 12, top: 25, paddingTop: 4, paddingBottom: 4 };

    return <section id={this.uiId} style={{ marginTop: '24px' }}>
      <section style={{ display: 'block', marginTop: '16px', position: 'relative' }}>
        <section>
          <FormattedMessage id="bluesnap.form.cardname.label">{
            text => <p style={style.label}>{text}</p>
          }</FormattedMessage>
          <div data-bluesnap="ccn" id="ccn" style={style.inputBox}/>
        </section>
        <Default>
          <img id="card-logo"
               style={imgStyle}
               src={logosPayments}
               draggable={false}/>
        </Default>
        <Mobile>
          <img id="card-logo"
               style={{ ...imgStyle, display: 'none' }}
               src={logosPayments}
               draggable={false}/>
        </Mobile>
        <FormattedMessage id="newOrder.checkout.bluesnap.ccn.error">{
          text => <p id="ccnError" style={style.errorLabel}>{text}</p>
        }</FormattedMessage>
      </section>
      <section id="wrapGroups">
        <section id="expGroup">
          <FormattedMessage id="bluesnap.form.date.label">{
            text => <p style={style.label}>{text}</p>
          }</FormattedMessage>
          <div data-bluesnap="exp" id="exp" style={style.inputBox}/>
          <FormattedMessage id="newOrder.checkout.bluesnap.exp.error">{
            text => <p id="expError" style={style.errorLabel}>{text}</p>
          }</FormattedMessage>
        </section>
        <section id="cvvGroup">
          <FormattedMessage id="bluesnap.form.date.cvs">{
            text => <p style={style.label}>{text}</p>
          }</FormattedMessage>
          <div data-bluesnap="cvv" id="cvv" style={style.inputBox}/>
          <FormattedMessage id="newOrder.checkout.bluesnap.cvv.error">{
            text => <p id="cvvError" style={style.errorLabel}>{text}</p>
          }</FormattedMessage>
        </section>
      </section>
    </section>;
  };

  clean = () => {
    if (document) {
      const $element = document.getElementById(this.uiId);
      if ($element) {
        const $parent = $element.parentNode;
        if ($parent) {
          try {
            // $parent.removeChild($element);
          } catch(e) {
            console.log('clean e', e);
          }
        }
      }
    }
  };

  showPopUp = (data: string) => undefined;
}



// WEBPACK FOOTER //
// ./src/modules/new-order/CheckoutStep/PaymentImpl/BlueSnap/index.js